import { useMemo, useRef, useState } from "react";
import { extractManyPdfs, extractBlobText } from "../pdf/extractText";

// Conflict & risk review scanner. Primary flow is INTERNAL: it renders the
// contract's own output PDF (exactly what gets exported), reads the text back
// with pdfjs, and sends it to /api/review-contract, which returns structured
// findings — internal conflicts, ambiguities, risky terms, deviations from the
// standard form and missing protections. An external PDF can also be uploaded.

type Severity = "critical" | "high" | "medium" | "low" | "info";
type Category =
  | "internal-conflict" | "ambiguity" | "risk-allocation" | "fidic-deviation"
  | "missing-provision" | "payment" | "time" | "compliance" | "unfavourable-term" | "drafting-error";

interface Finding {
  severity: Severity;
  category: Category;
  title: string;
  location?: string;
  excerptA?: string;
  excerptB?: string;
  issue: string;
  recommendation?: string;
}

interface Review {
  documentTitle?: string;
  contractForm?: string;
  overallRisk: "low" | "medium" | "high" | "critical";
  summary: string;
  findings: Finding[];
}

const SEV_ORDER: Severity[] = ["critical", "high", "medium", "low", "info"];
const SEV_LABEL: Record<Severity, string> = { critical: "Critical", high: "High", medium: "Medium", low: "Low", info: "Info" };
const CAT_LABEL: Record<Category, string> = {
  "internal-conflict": "Internal conflict",
  ambiguity: "Ambiguity",
  "risk-allocation": "Risk allocation",
  "fidic-deviation": "Form deviation",
  "missing-provision": "Missing provision",
  payment: "Payment",
  time: "Time",
  compliance: "Compliance",
  "unfavourable-term": "Unfavourable term",
  "drafting-error": "Drafting error",
};

interface FixProposal {
  changeSummary?: string;
  fixed: { finding: string; how: string }[];
  deferred: { finding: string; why: string }[];
  patch: any;
}

export function ConflictScanner({
  renderOutputPdf,
  outputLabel = "This contract (output PDF)",
  brief,
  onApplyFixes,
}: {
  renderOutputPdf?: () => Promise<Blob>;
  outputLabel?: string;
  brief?: any;
  onApplyFixes?: (patch: any) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [perspective, setPerspective] = useState<"neutral" | "employer" | "contractor">("neutral");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [fileNames, setFileNames] = useState<string[]>([]);
  // Smart fix
  const [fixBusy, setFixBusy] = useState(false);
  const [fixErr, setFixErr] = useState<string | null>(null);
  const [proposal, setProposal] = useState<FixProposal | null>(null);
  const [applied, setApplied] = useState<FixProposal | null>(null);
  // Whether this review came from the current in-app draft (fixable) vs an upload.
  const [internal, setInternal] = useState(false);

  async function runReview(text: string, sources: string[]) {
    if (!text.trim()) {
      setErr("No selectable text found (the PDFs may be scanned images). Try a vector PDF exported from Word.");
      return;
    }
    setBusy(true);
    setErr(null);
    setReview(null);
    setProposal(null);
    setApplied(null);
    setFixErr(null);
    setFileNames(sources);
    setStatus(`Reviewing ${text.length.toLocaleString()} characters for conflicts and risks…`);
    try {
      const res = await fetch("/api/review-contract", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, perspective }),
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const data = (await res.json()) as { review?: Review };
      if (!data.review) throw new Error("No review returned.");
      // Normalise: the model occasionally omits or malforms `findings` — never let
      // a non-array reach the render (it would throw on [...findings] and blank the app).
      setReview({ ...data.review, findings: Array.isArray(data.review.findings) ? data.review.findings : [] });
      setStatus(null);
    } catch (e: any) {
      setErr(e?.message || "Review failed.");
      setStatus(null);
    } finally {
      setBusy(false);
    }
  }

  async function handleFiles(files: FileList | null) {
    if (!files || !files.length) return;
    setBusy(true);
    setErr(null);
    setStatus(`Reading ${files.length} file(s)…`);
    try {
      setInternal(false);
      const results = await extractManyPdfs(Array.from(files));
      const combined = results.map((r) => r.text).join("\n\n");
      await runReview(combined, results.map((r) => r.fileName));
    } catch (e: any) {
      setErr(e?.message || "Could not read the files.");
      setStatus(null);
      setBusy(false);
    }
  }

  async function scanInternal() {
    if (!renderOutputPdf) return;
    setInternal(true);
    setBusy(true);
    setErr(null);
    setReview(null);
    setStatus("Rendering the contract's output PDF…");
    try {
      const blob = await renderOutputPdf();
      setStatus("Reading the exported PDF back…");
      const extracted = await extractBlobText(blob, outputLabel);
      if (!extracted.text.trim()) {
        setErr("Could not read text from the rendered PDF. Try exporting it and uploading the file instead.");
        setStatus(null);
        setBusy(false);
        return;
      }
      await runReview(extracted.text, [`${outputLabel} — ${extracted.pages} page${extracted.pages === 1 ? "" : "s"}`]);
    } catch (e: any) {
      setErr(e?.message || "Could not render the contract PDF.");
      setStatus(null);
      setBusy(false);
    }
  }

  async function runSmartFix() {
    if (!review || !brief) return;
    setFixBusy(true);
    setFixErr(null);
    setProposal(null);
    try {
      const res = await fetch("/api/fix-contract", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ findings: review.findings, brief, perspective }),
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const data = (await res.json()) as FixProposal;
      setProposal({ ...data, fixed: data.fixed || [], deferred: data.deferred || [], patch: data.patch || {} });
    } catch (e: any) {
      setFixErr(e?.message || "Smart fix failed.");
    } finally {
      setFixBusy(false);
    }
  }

  function applyProposal() {
    if (!proposal || !onApplyFixes) return;
    onApplyFixes(proposal.patch);
    setApplied(proposal);
    setProposal(null);
  }

  const sorted = useMemo(
    () => (review && Array.isArray(review.findings) ? [...review.findings].sort((a, b) => SEV_ORDER.indexOf(a.severity) - SEV_ORDER.indexOf(b.severity)) : []),
    [review],
  );
  const counts = useMemo(() => {
    const c: Partial<Record<Severity, number>> = {};
    for (const f of sorted) c[f.severity] = (c[f.severity] || 0) + 1;
    return c;
  }, [sorted]);

  return (
    <div className="scanner">
      <div className="scanner-intro">
        <h2>Conflict &amp; risk review scanner</h2>
        <p className="muted">
          Reads <strong>this contract's own exported PDF</strong> — exactly what you'd send out — and reviews it as a whole:
          internal conflicts (figures, dates, %s, VAT, names that disagree across the documents), ambiguities, one-sided or
          risky terms, deviations from the standard form, and missing protections. Nothing is stored.
        </p>
      </div>

      <div className="scanner-controls">
        <label className="field">
          <span>Review on behalf of</span>
          <select value={perspective} onChange={(e) => setPerspective(e.target.value as any)}>
            <option value="neutral">Neutral (both parties)</option>
            <option value="employer">Employer / Owner</option>
            <option value="contractor">Contractor</option>
          </select>
        </label>

        <div className="scanner-actions">
          {renderOutputPdf && (
            <button className="dark" disabled={busy} onClick={scanInternal} title="Render this contract's output PDF and scan it for inconsistencies">
              {busy ? <span className="spinner" /> : "⌕ Scan this contract"}
            </button>
          )}
        </div>
      </div>

      <div className="scanner-alt">
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={(e) => handleFiles(e.target.files)}
        />
        <button className="link" disabled={busy} onClick={() => inputRef.current?.click()}>
          or review an external PDF instead →
        </button>
      </div>

      {status && <div className="scanner-status">{status}</div>}
      {err && <div className="scanner-error">{err}</div>}

      {review && (
        <div className="review-report">
          <div className={`review-head risk-${review.overallRisk}`}>
            <div>
              <div className="review-title">{review.documentTitle || "Contract review"}</div>
              <div className="review-form">
                {review.contractForm ? review.contractForm + " · " : ""}
                {fileNames.join(", ")}
              </div>
            </div>
            <div className="review-risk">
              <span className="risk-badge">{review.overallRisk.toUpperCase()} RISK</span>
              <span className="risk-count">
                {sorted.length} finding{sorted.length === 1 ? "" : "s"}
              </span>
            </div>
          </div>

          <p className="review-summary">{review.summary}</p>

          {sorted.length > 0 && (
            <div className="review-tallies">
              {SEV_ORDER.filter((s) => counts[s]).map((s) => (
                <span key={s} className={`sev-chip sev-${s}`}>
                  {counts[s]} {SEV_LABEL[s]}
                </span>
              ))}
            </div>
          )}

          {sorted.length === 0 ? (
            <div className="review-clean">✓ No conflicts or material risks detected.</div>
          ) : (
            <ol className="finding-list">
              {sorted.map((f, i) => (
                <li key={i} className={`finding sev-border-${f.severity}`}>
                  <div className="finding-top">
                    <span className={`sev-chip sev-${f.severity}`}>{SEV_LABEL[f.severity]}</span>
                    <span className="cat-chip">{CAT_LABEL[f.category] || f.category}</span>
                    {f.location && <span className="finding-loc">{f.location}</span>}
                  </div>
                  <div className="finding-title">{f.title}</div>
                  {(f.excerptA || f.excerptB) && (
                    <div className="finding-excerpts">
                      {f.excerptA && <blockquote>{f.excerptA}</blockquote>}
                      {f.excerptB && (
                        <>
                          <div className="vs">conflicts with</div>
                          <blockquote>{f.excerptB}</blockquote>
                        </>
                      )}
                    </div>
                  )}
                  <div className="finding-issue">{f.issue}</div>
                  {f.recommendation && (
                    <div className="finding-fix">
                      <span className="fix-label">Recommendation</span> {f.recommendation}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}

          {/* Smart fix — only for an internal scan of the editable draft */}
          {internal && onApplyFixes && sorted.length > 0 && !applied && (
            <div className="smartfix">
              {!proposal ? (
                <div className="smartfix-cta">
                  <button className="dark" disabled={fixBusy} onClick={runSmartFix}>
                    {fixBusy ? <span className="spinner" /> : "✨ Smart fix"}
                  </button>
                  <span className="muted">Let the AI rewrite the affected clauses to resolve the fixable findings — you review the changes before they apply.</span>
                </div>
              ) : (
                <div className="smartfix-proposal">
                  <div className="sf-head">✨ Proposed fixes</div>
                  {proposal.changeSummary && <p className="sf-summary">{proposal.changeSummary}</p>}
                  {proposal.fixed.length > 0 && (
                    <>
                      <div className="sf-sub">Will fix ({proposal.fixed.length})</div>
                      <ul className="sf-list sf-fixed">
                        {proposal.fixed.map((f, i) => (
                          <li key={i}><span className="sf-title">{f.finding}</span> — {f.how}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {proposal.deferred.length > 0 && (
                    <>
                      <div className="sf-sub">Left for your decision ({proposal.deferred.length})</div>
                      <ul className="sf-list sf-deferred">
                        {proposal.deferred.map((f, i) => (
                          <li key={i}><span className="sf-title">{f.finding}</span> — {f.why}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  <div className="sf-actions">
                    <button className="dark" disabled={!proposal.fixed.length} onClick={applyProposal}>
                      Apply {proposal.fixed.length} fix{proposal.fixed.length === 1 ? "" : "es"}
                    </button>
                    <button onClick={() => setProposal(null)}>Cancel</button>
                  </div>
                </div>
              )}
              {fixErr && <div className="scanner-error" style={{ marginTop: 10 }}>{fixErr}</div>}
            </div>
          )}
          {applied && (
            <div className="smartfix-done">
              ✓ Applied {applied.fixed.length} fix{applied.fixed.length === 1 ? "" : "es"} to your contract and saved.
              {applied.deferred.length > 0 && <> {applied.deferred.length} item{applied.deferred.length === 1 ? "" : "s"} left for your decision.</>}
              {renderOutputPdf && (
                <button className="dark" style={{ marginLeft: 12 }} disabled={busy} onClick={scanInternal}>
                  {busy ? <span className="spinner" /> : "⌕ Re-scan to verify"}
                </button>
              )}
            </div>
          )}

          <div className="review-actions">
            <button onClick={() => window.print()}>⎙ Print / save report</button>
            <button onClick={() => { setReview(null); setFileNames([]); setProposal(null); setApplied(null); }}>Review another</button>
          </div>
          <p className="review-disclaimer muted">
            An AI-assisted review to help you spot issues — not legal advice. Verify each finding against the source document.
          </p>
        </div>
      )}
    </div>
  );
}
